import { apiClient } from './client';
import type { MockMaintenanceSchedule, MockMaintenanceWorkOrder, MockAMCContract } from '@/mocks/maintenance';

export interface MaintenanceSparePart {
  id: string;
  partCode: string;
  partName: string;
  category: string;
  compatibleMachines: { id: string; name: string }[];
  stockQty: number;
  unit: string;
  unitCost: number;
  minStockLevel: number;
  rackLocation: string;
  vendorName: string;
  description: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export const maintenanceApi = {
  // Schedules
  getSchedules: async (warehouseId?: string): Promise<MockMaintenanceSchedule[]> => {
    const params = warehouseId ? { warehouse_id: warehouseId } : undefined;
    const res = await apiClient.get('/api/v1/manufacturing/maintenance/schedules', { params });
    return (res.data?.data || []).map((s: any) => ({
      id: s.id,
      machineId: s.machine_id,
      machineName: s.machine_name,
      scheduleType: s.schedule_type,
      frequencyDays: s.frequency_days ?? null,
      taskName: s.task_name,
      description: s.description || '',
      estimatedDurationMinutes: s.estimated_duration_minutes || 60,
      assignedTo: s.assigned_to_name || 'Technician',
      tasks: typeof s.tasks === 'string' ? JSON.parse(s.tasks) : (s.tasks || []),
      lastDoneDate: s.last_done_date ? s.last_done_date.slice(0, 10) : null,
      nextDueDate: s.next_due_date ? s.next_due_date.slice(0, 10) : '',
      advanceAlertDays: s.advance_alert_days || 7,
      isActive: s.is_active ?? true
    }));
  },

  createSchedule: async (payload: any): Promise<MockMaintenanceSchedule> => {
    const res = await apiClient.post('/api/v1/manufacturing/maintenance/schedules', payload);
    const s = res.data?.data;
    return {
      id: s.id,
      machineId: s.machine_id,
      machineName: s.machine_name,
      scheduleType: s.schedule_type,
      frequencyDays: s.frequency_days ?? null,
      taskName: s.task_name,
      description: s.description || '',
      estimatedDurationMinutes: s.estimated_duration_minutes || 60,
      assignedTo: s.assigned_to_name || 'Technician',
      tasks: typeof s.tasks === 'string' ? JSON.parse(s.tasks) : (s.tasks || []),
      lastDoneDate: s.last_done_date ? s.last_done_date.slice(0, 10) : null,
      nextDueDate: s.next_due_date ? s.next_due_date.slice(0, 10) : '',
      advanceAlertDays: s.advance_alert_days || 7,
      isActive: s.is_active ?? true
    };
  },

  toggleScheduleActive: async (id: string): Promise<MockMaintenanceSchedule> => {
    const res = await apiClient.patch(`/api/v1/manufacturing/maintenance/schedules/${id}/toggle-active`);
    const s = res.data?.data;
    return {
      id: s.id,
      machineId: s.machine_id,
      machineName: s.machine_name,
      scheduleType: s.schedule_type,
      frequencyDays: s.frequency_days ?? null,
      taskName: s.task_name,
      description: s.description || '',
      estimatedDurationMinutes: s.estimated_duration_minutes || 60,
      assignedTo: s.assigned_to_name || 'Technician',
      tasks: typeof s.tasks === 'string' ? JSON.parse(s.tasks) : (s.tasks || []),
      lastDoneDate: s.last_done_date ? s.last_done_date.slice(0, 10) : null,
      nextDueDate: s.next_due_date ? s.next_due_date.slice(0, 10) : '',
      advanceAlertDays: s.advance_alert_days || 7,
      isActive: s.is_active ?? true
    };
  },

  // Work Orders (MWO)
  getWorkOrders: async (warehouseId?: string): Promise<MockMaintenanceWorkOrder[]> => {
    const params = warehouseId ? { warehouse_id: warehouseId } : undefined;
    const res = await apiClient.get('/api/v1/manufacturing/maintenance/work-orders', { params });
    return (res.data?.data || []).map((m: any) => ({
      id: m.id,
      mwoNumber: m.mwo_number,
      scheduleId: m.schedule_id,
      machineId: m.machine_id,
      machineName: m.machine_name,
      scheduleType: m.schedule_type,
      taskName: m.task_name,
      scheduledDate: m.scheduled_date ? m.scheduled_date.slice(0, 10) : '',
      status: m.status,
      assignedTo: m.assigned_to_name || 'Technician',
      actualStartTime: m.actual_start_time,
      actualEndTime: m.actual_end_time,
      durationMinutes: m.duration_minutes,
      completedTasks: typeof m.completed_tasks === 'string' ? JSON.parse(m.completed_tasks) : (m.completed_tasks || []),
      sparesUsed: typeof m.spares_used === 'string' ? JSON.parse(m.spares_used) : (m.spares_used || []),
      findings: m.findings,
      nextActionRequired: m.next_action_required,
      skipReason: m.skip_reason,
      completedBy: m.completed_by,
      completedAt: m.completed_at
    }));
  },

  createWorkOrder: async (payload: any): Promise<MockMaintenanceWorkOrder> => {
    const res = await apiClient.post('/api/v1/manufacturing/maintenance/work-orders', payload);
    const m = res.data?.data;
    return {
      id: m.id,
      mwoNumber: m.mwo_number,
      scheduleId: m.schedule_id,
      machineId: m.machine_id,
      machineName: m.machine_name,
      scheduleType: m.schedule_type,
      taskName: m.task_name,
      scheduledDate: m.scheduled_date ? m.scheduled_date.slice(0, 10) : '',
      status: m.status,
      assignedTo: m.assigned_to_name || 'Technician',
      actualStartTime: m.actual_start_time,
      actualEndTime: m.actual_end_time,
      durationMinutes: m.duration_minutes,
      completedTasks: typeof m.completed_tasks === 'string' ? JSON.parse(m.completed_tasks) : (m.completed_tasks || []),
      sparesUsed: typeof m.spares_used === 'string' ? JSON.parse(m.spares_used) : (m.spares_used || []),
      findings: m.findings,
      nextActionRequired: m.next_action_required,
      skipReason: m.skip_reason,
      completedBy: m.completed_by,
      completedAt: m.completed_at
    };
  },

  startWorkOrder: async (id: string): Promise<MockMaintenanceWorkOrder> => {
    const res = await apiClient.patch(`/api/v1/manufacturing/maintenance/work-orders/${id}/start`);
    const m = res.data?.data;
    return {
      id: m.id,
      mwoNumber: m.mwo_number,
      scheduleId: m.schedule_id,
      machineId: m.machine_id,
      machineName: m.machine_name,
      scheduleType: m.schedule_type,
      taskName: m.task_name,
      scheduledDate: m.scheduled_date ? m.scheduled_date.slice(0, 10) : '',
      status: m.status,
      assignedTo: m.assigned_to_name || 'Technician',
      actualStartTime: m.actual_start_time,
      actualEndTime: m.actual_end_time,
      durationMinutes: m.duration_minutes,
      completedTasks: typeof m.completed_tasks === 'string' ? JSON.parse(m.completed_tasks) : (m.completed_tasks || []),
      sparesUsed: typeof m.spares_used === 'string' ? JSON.parse(m.spares_used) : (m.spares_used || []),
      findings: m.findings,
      nextActionRequired: m.next_action_required,
      skipReason: m.skip_reason,
      completedBy: m.completed_by,
      completedAt: m.completed_at
    };
  },

  completeWorkOrder: async (id: string, payload: any): Promise<MockMaintenanceWorkOrder> => {
    const res = await apiClient.patch(`/api/v1/manufacturing/maintenance/work-orders/${id}/complete`, payload);
    const m = res.data?.data;
    return {
      id: m.id,
      mwoNumber: m.mwo_number,
      scheduleId: m.schedule_id,
      machineId: m.machine_id,
      machineName: m.machine_name,
      scheduleType: m.schedule_type,
      taskName: m.task_name,
      scheduledDate: m.scheduled_date ? m.scheduled_date.slice(0, 10) : '',
      status: m.status,
      assignedTo: m.assigned_to_name || 'Technician',
      actualStartTime: m.actual_start_time,
      actualEndTime: m.actual_end_time,
      durationMinutes: m.duration_minutes,
      completedTasks: typeof m.completed_tasks === 'string' ? JSON.parse(m.completed_tasks) : (m.completed_tasks || []),
      sparesUsed: typeof m.spares_used === 'string' ? JSON.parse(m.spares_used) : (m.spares_used || []),
      findings: m.findings,
      nextActionRequired: m.next_action_required,
      skipReason: m.skip_reason,
      completedBy: m.completed_by,
      completedAt: m.completed_at
    };
  },

  skipWorkOrder: async (id: string, skipReason: string): Promise<MockMaintenanceWorkOrder> => {
    const res = await apiClient.patch(`/api/v1/manufacturing/maintenance/work-orders/${id}/skip`, { skip_reason: skipReason });
    const m = res.data?.data;
    return {
      id: m.id,
      mwoNumber: m.mwo_number,
      scheduleId: m.schedule_id,
      machineId: m.machine_id,
      machineName: m.machine_name,
      scheduleType: m.schedule_type,
      taskName: m.task_name,
      scheduledDate: m.scheduled_date ? m.scheduled_date.slice(0, 10) : '',
      status: m.status,
      assignedTo: m.assigned_to_name || 'Technician',
      actualStartTime: m.actual_start_time,
      actualEndTime: m.actual_end_time,
      durationMinutes: m.duration_minutes,
      completedTasks: typeof m.completed_tasks === 'string' ? JSON.parse(m.completed_tasks) : (m.completed_tasks || []),
      sparesUsed: typeof m.spares_used === 'string' ? JSON.parse(m.spares_used) : (m.spares_used || []),
      findings: m.findings,
      nextActionRequired: m.next_action_required,
      skipReason: m.skip_reason,
      completedBy: m.completed_by,
      completedAt: m.completed_at
    };
  },

  // AMC Contracts
  getAMCContracts: async (warehouseId?: string): Promise<MockAMCContract[]> => {
    const params = warehouseId && warehouseId !== 'ALL' ? { warehouse_id: warehouseId } : undefined;
    const res = await apiClient.get('/api/v1/manufacturing/maintenance/amc', { params });
    return (res.data?.data || []).map((a: any) => ({
      id: a.id,
      warehouseId: a.warehouse_id,
      warehouse_id: a.warehouse_id,
      contractNumber: a.contract_number,
      vendorName: a.vendor_name,
      vendorPhone: a.vendor_phone || '',
      vendorEmail: a.vendor_email || '',
      machineIds: typeof a.machine_ids === 'string' ? JSON.parse(a.machine_ids) : (a.machine_ids || []),
      machineNames: typeof a.machine_names === 'string' ? JSON.parse(a.machine_names) : (a.machine_names || []),
      contractType: a.contract_type,
      startDate: a.start_date ? a.start_date.slice(0, 10) : '',
      endDate: a.end_date ? a.end_date.slice(0, 10) : '',
      annualCost: parseFloat(a.annual_cost || 0),
      visitFrequency: a.visit_frequency || 'Monthly',
      nextVisitDate: a.next_visit_date ? a.next_visit_date.slice(0, 10) : null,
      status: a.status
    }));
  },

  createAMCContract: async (payload: any): Promise<MockAMCContract> => {
    const res = await apiClient.post('/api/v1/manufacturing/maintenance/amc', payload);
    const a = res.data?.data;
    return {
      id: a.id,
      warehouseId: a.warehouse_id,
      warehouse_id: a.warehouse_id,
      contractNumber: a.contract_number,
      vendorName: a.vendor_name,
      vendorPhone: a.vendor_phone || '',
      vendorEmail: a.vendor_email || '',
      machineIds: typeof a.machine_ids === 'string' ? JSON.parse(a.machine_ids) : (a.machine_ids || []),
      machineNames: typeof a.machine_names === 'string' ? JSON.parse(a.machine_names) : (a.machine_names || []),
      contractType: a.contract_type,
      startDate: a.start_date ? a.start_date.slice(0, 10) : '',
      endDate: a.end_date ? a.end_date.slice(0, 10) : '',
      annualCost: parseFloat(a.annual_cost || 0),
      visitFrequency: a.visit_frequency || 'Monthly',
      nextVisitDate: a.next_visit_date ? a.next_visit_date.slice(0, 10) : null,
      status: a.status
    };
  },

  // Spare Parts
  getSpareParts: async (warehouseId?: string): Promise<MaintenanceSparePart[]> => {
    const params = warehouseId && warehouseId !== 'ALL' ? { warehouse_id: warehouseId } : undefined;
    const res = await apiClient.get('/api/v1/manufacturing/maintenance/spares', { params });
    return (res.data?.data || []).map((sp: any) => ({
      id: sp.id,
      warehouseId: sp.warehouse_id,
      warehouse_id: sp.warehouse_id,
      partCode: sp.part_code,
      partName: sp.part_name,
      category: sp.category || 'MECHANICAL',
      compatibleMachines: typeof sp.compatible_machines === 'string' ? JSON.parse(sp.compatible_machines) : (sp.compatible_machines || []),
      stockQty: parseInt(sp.stock_qty || 0, 10),
      unit: sp.unit || 'Pcs',
      unitCost: parseFloat(sp.unit_cost || 0),
      minStockLevel: parseInt(sp.min_stock_level || 5, 10),
      rackLocation: sp.rack_location || '',
      vendorName: sp.vendor_name || '',
      description: sp.description || '',
      isActive: sp.is_active ?? true,
      createdAt: sp.created_at,
      updatedAt: sp.updated_at
    }));
  },

  createSparePart: async (payload: any): Promise<MaintenanceSparePart> => {
    const res = await apiClient.post('/api/v1/manufacturing/maintenance/spares', payload);
    const sp = res.data?.data;
    return {
      id: sp.id,
      partCode: sp.part_code,
      partName: sp.part_name,
      category: sp.category || 'MECHANICAL',
      compatibleMachines: typeof sp.compatible_machines === 'string' ? JSON.parse(sp.compatible_machines) : (sp.compatible_machines || []),
      stockQty: parseInt(sp.stock_qty || 0, 10),
      unit: sp.unit || 'Pcs',
      unitCost: parseFloat(sp.unit_cost || 0),
      minStockLevel: parseInt(sp.min_stock_level || 5, 10),
      rackLocation: sp.rack_location || '',
      vendorName: sp.vendor_name || '',
      description: sp.description || '',
      isActive: sp.is_active ?? true,
      createdAt: sp.created_at,
      updatedAt: sp.updated_at
    };
  },

  updateSparePart: async (id: string, payload: any): Promise<MaintenanceSparePart> => {
    const res = await apiClient.put(`/api/v1/manufacturing/maintenance/spares/${id}`, payload);
    const sp = res.data?.data;
    return {
      id: sp.id,
      partCode: sp.part_code,
      partName: sp.part_name,
      category: sp.category || 'MECHANICAL',
      compatibleMachines: typeof sp.compatible_machines === 'string' ? JSON.parse(sp.compatible_machines) : (sp.compatible_machines || []),
      stockQty: parseInt(sp.stock_qty || 0, 10),
      unit: sp.unit || 'Pcs',
      unitCost: parseFloat(sp.unit_cost || 0),
      minStockLevel: parseInt(sp.min_stock_level || 5, 10),
      rackLocation: sp.rack_location || '',
      vendorName: sp.vendor_name || '',
      description: sp.description || '',
      isActive: sp.is_active ?? true,
      createdAt: sp.created_at,
      updatedAt: sp.updated_at
    };
  },

  restockSparePart: async (id: string, addQty: number): Promise<MaintenanceSparePart> => {
    const res = await apiClient.patch(`/api/v1/manufacturing/maintenance/spares/${id}/restock`, { add_qty: addQty });
    const sp = res.data?.data;
    return {
      id: sp.id,
      partCode: sp.part_code,
      partName: sp.part_name,
      category: sp.category || 'MECHANICAL',
      compatibleMachines: typeof sp.compatible_machines === 'string' ? JSON.parse(sp.compatible_machines) : (sp.compatible_machines || []),
      stockQty: parseInt(sp.stock_qty || 0, 10),
      unit: sp.unit || 'Pcs',
      unitCost: parseFloat(sp.unit_cost || 0),
      minStockLevel: parseInt(sp.min_stock_level || 5, 10),
      rackLocation: sp.rack_location || '',
      vendorName: sp.vendor_name || '',
      description: sp.description || '',
      isActive: sp.is_active ?? true,
      createdAt: sp.created_at,
      updatedAt: sp.updated_at
    };
  },

  deleteSparePart: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/manufacturing/maintenance/spares/${id}`);
  },

  // Master Units API Integration
  getMasterUnits: async (): Promise<Array<{ id: string; name: string; shortName: string }>> => {
    try {
      const res = await apiClient.get('/api/v1/masters/units/all');
      return (res.data?.data || []).map((u: any) => ({
        id: u.id,
        name: u.name,
        shortName: u.shortName || u.name
      }));
    } catch (err) {
      return [
        { id: '1', name: 'Pieces', shortName: 'Pcs' },
        { id: '2', name: 'Liters', shortName: 'Ltr' },
        { id: '3', name: 'Kilograms', shortName: 'Kg' },
        { id: '4', name: 'Meters', shortName: 'Mtr' },
        { id: '5', name: 'Sets', shortName: 'Set' },
        { id: '6', name: 'Boxes', shortName: 'Box' }
      ];
    }
  }
};
